/**
 * Pitch Deck Content — v4: Partner & Broker Prospectus
 * ──────────────────────────────────────────────────────
 * Audience: AFSL holders, wealth tech platforms, SMSF administrators,
 *           boutique wealth managers, multi-family offices, B2B fintechs.
 * Framing:  Technology partner / CAR sub-advisor, not retail SaaS.
 */

import ehsanThumb from "../assets/ehsan_thmb.jpg";
import erfanThumb from "../assets/erfan_thmb.jpg";
import geoffThumb from "../assets/geoff_thmb.jpg";

const pitchDeck = {
  meta: {
    title: "Paxia.ai — Partnership Prospectus",
    subtitle: "Confidential — For Partnership Evaluation",
    date: "2026",
    accessCode: "paxia2025",
  },

  sections: [
    /* ── 0  HERO ── */
    {
      id: "hero",
      type: "hero",
      title: "Paxia.ai",
      subtitle: "AI-Driven Trading Research for Brokers & Partners",
      cta: "Scroll to explore ↓",
    },

    /* ── 1  PROBLEM ── */
    {
      id: "problem",
      type: "problem",
      label: "The Gap",
      title: "Wealth platforms are failing sophisticated clients.",
      lead: "High-net-worth investors, family offices, and SMSF trustees are demanding institutional-grade research and execution. Existing platforms offer static portfolios or opaque automation — neither is fit for purpose.",
      painPoints: [
        {
          icon: "ShieldAlert",
          title: "No fiduciary-safe automation",
          body: "Pure algorithmic execution carries significant compliance and fiduciary risk. Partners are exposed when AI acts without human oversight. Regulators and clients both require visible, auditable decision chains.",
          tooltip: "MDA regulations under the Corporations Act require that discretionary portfolio management decisions are properly supervised. Fully autonomous AI trading without human sign-off creates clear liability exposure for AFSL holders.",
        },
        {
          icon: "Brain",
          title: "Static portfolios can't compete",
          body: "Traditional wealth platforms offer model portfolios and rebalancing. Sophisticated clients are migrating to services that deliver systematic, data-driven research and adaptive execution — capabilities most platforms cannot build in-house.",
          tooltip: "Cerulli Associates reports that 68% of HNW clients under 50 expect algorithmic or AI-assisted investment management. Static model portfolios are a retention risk for premium client segments.",
        },
        {
          icon: "Code",
          title: "Quantitative capability is expensive to build",
          body: "Building a proprietary algorithmic research platform requires quant talent, data infrastructure, compliance architecture, and broker API integration. Total build cost for a functional system is $2M–$5M+, with ongoing maintenance.",
          tooltip: "Estimates based on typical build-out costs for institutional-grade quant platforms: data feeds ($200K+/yr), quant engineering team ($800K–1.5M/yr), compliance overlay ($150K+/yr), broker integration ($200–400K build).",
        },
        {
          icon: "LayoutGrid",
          title: "Fragmented execution infrastructure",
          body: "Partners managing clients across IBKR, MT5, and cTrader must reconcile positions, risk, and reporting manually. Operational overhead reduces capacity to serve clients and scale AUM.",
          tooltip: "Operational drag from fragmented broker infrastructure is one of the top three costs for boutique wealth managers, according to Deloitte's 2024 Wealth Management Outlook.",
        },
        {
          icon: "Monitor",
          title: "Client retention is under pressure",
          body: "Premium brokers and fintech platforms are offering AI-enhanced research to attract HNW clients. Partners without comparable capability risk losing their highest-value accounts.",
          tooltip: "Industry data shows that HNW clients with access to AI-assisted portfolio tools have 22% higher retention rates (PwC Global Wealth Report 2024). AI research capability is becoming a retention-critical feature.",
        },
      ],
      stat: {
        value: "$2M–5M+",
        label: "typical cost to build equivalent in-house capability",
        source: "Industry estimates for institutional quant platform build-out",
      },
    },

    /* ── 2  SOLUTION ── */
    {
      id: "solution",
      type: "solution",
      label: "The Solution",
      title: "Institutional-grade AI research. Compliance-first architecture. Partner-ready.",
      capabilities: [
        {
          icon: "ShieldCheck",
          title: "Human-in-the-Loop Compliance Architecture",
          summary: "All AI-generated research and proposed execution parameters require explicit human authorisation before any order is routed. Fiduciary safety by design.",
          detail: "Paxia.ai is built on a strict human-in-the-loop model. The AI surfaces research, signals, and proposed trade parameters. A human — the adviser, portfolio manager, or client — must explicitly approve before the order is transmitted to IBKR, MT5, or cTrader. Every decision is logged with a full audit trail. This preserves your compliance oversight and satisfies MDA/SMA regulatory requirements.",
          benefit: "Partners retain full fiduciary accountability. Regulators can audit every decision point. Liability exposure from autonomous AI action is eliminated.",
          image: "risk-settings",
        },
        {
          icon: "BrainCircuit",
          title: "AI Research & Signal Generation",
          summary: "Systematic, AI-generated trading research across equities, ETFs, CFDs, and structured derivatives — delivered via API or white-label interface.",
          detail: "Paxia.ai generates structured trading research using a combination of technical pattern recognition, fundamental screening, news sentiment analysis, and economic calendar events. Research outputs include proposed entry/exit parameters, risk-adjusted position sizing, and strategy rationale. All designed for mid-to-low frequency models — stable across standard IBKR connections, not HFT-dependent.",
          benefit: "Partners can offer institutional-quality research to clients without building a quant team. Research is explainable and auditable — suitable for adviser review before client presentation.",
          image: "ai-assisted",
        },
        {
          icon: "Layers",
          title: "Multi-Broker Execution Infrastructure",
          summary: "Integrated with IBKR, MetaTrader 5, and cTrader. End-users execute via their own accounts. Partner never touches client funds.",
          detail: "Paxia.ai connects to the client's own broker account via API. Orders route directly to their broker — Paxia never holds, transfers, or has custody of client funds at any stage. This is an architectural constraint, not a policy. Supports IBKR (global equities, ETFs, derivatives), MT5 (CFD/FX), and cTrader (CFD/FX). Additional brokers on the integration roadmap.",
          benefit: "No client money risk for the partner. Clients retain their existing broker relationships. Partners can onboard clients using accounts they already hold.",
          image: "multi-broker",
        },
        {
          icon: "LayoutGrid",
          title: "White-Label Research Interface",
          summary: "Deploy Paxia's research and strategy workspace under your brand. Fully configurable UI for your client segment.",
          detail: "Partners can deploy the full Paxia.ai interface — charts, research outputs, trade management, risk controls, journaling — under their own brand. The API-first architecture allows deep integration with existing CRM, portfolio management, and compliance systems. Client-facing UI is configurable for retail, HNW, or institutional presentation.",
          benefit: "Revenue from premium research access flows under your brand. Clients experience a seamless, proprietary-feeling platform. No white-label branding constraints.",
          image: "workspace",
        },
        {
          icon: "Bot",
          title: "Strategy Automation Without Code",
          summary: "Describe a strategy in plain language, generate a testable bot, deploy with enforced risk controls. No programming required.",
          detail: "Advisers and portfolio managers can define investment strategies in natural language. The system generates structured, testable strategy configurations that can be backtested, reviewed, and deployed. All automated execution remains within enforced risk guardrails set by the partner or client.",
          benefit: "Partners can systematise their investment process without quant resource. Strategies are documented, reproducible, and auditable — meeting compliance requirements for systematic approaches.",
          image: "no-code-bot",
        },
      ],
    },

    /* ── 3  PARTNERSHIP MODELS ── */
    {
      id: "business-model",
      type: "businessModel",
      label: "Partnership Models",
      title: "Two flexible partnership structures — aligned to your regulatory framework",
      tiers: [
        {
          name: "Model A: White-Label Integration",
          price: "SaaS licence + rev-share",
          features: [
            "Full platform access via API",
            "White-label UI under partner brand",
            "Research & signal generation",
            "Risk controls configurable per client",
            "IBKR, MT5 & cTrader execution",
          ],
          ideal: "B2B fintechs, wealth tech platforms, and SMSF administrators seeking to embed AI research capability into their existing client interface.",
        },
        {
          name: "Model B: CAR / Managed Account",
          price: "AUM bps + performance fee",
          highlight: true,
          features: [
            "Paxia.ai as research provider or sub-adviser",
            "MDA / SMA execution via IBKR",
            "Human-approval layer preserved throughout",
            "Full audit trail for compliance",
            "Co-branded client reporting",
          ],
          ideal: "AFSL holders, boutique wealth managers, and multi-family offices running Managed Discretionary Accounts or Separately Managed Accounts on behalf of clients.",
        },
        {
          name: "Model C: Broker Technology Partner",
          price: "Volume-based licensing",
          features: [
            "Paxia platform as value-add for broker clients",
            "Co-marketing to existing client base",
            "Revenue share on platform subscriptions",
            "Proprietary indicator & strategy library",
            "Reduced churn for high-value accounts",
          ],
          ideal: "AFSL-licensed brokers (MT5/cTrader ecosystem) seeking to reduce client churn, increase engagement, and add a differentiated research capability for algorithmic traders.",
        },
      ],
      modules: [
        "Annual SaaS licensing fee for enterprise API access",
        "Tiered revenue share on premium client subscriptions (e.g. 70/30 in partner's favour)",
        "Basis point fee on AUM driven by Paxia models (e.g. 15–25 bps)",
        "Performance fee split on model-generated returns (e.g. 20% of performance fee)",
        "Co-branded client onboarding and white-label setup",
        "Dedicated integration engineering support",
        "Priority access to new broker integrations and feature releases",
        "Joint go-to-market and referral programs",
      ],
      additionalRevenue: [
        {
          title: "Global reach via IBKR",
          body: "IBKR's global infrastructure means partners are not constrained to the Australian market. Models can be deployed across US, European, and Asian equities immediately — expanding the addressable market for any partner.",
        },
        {
          title: "Client retention uplift",
          body: "Offering AI-driven portfolio research is a proven retention tool for high-value client segments. HNW clients and SMSF trustees with access to systematic research tools show materially higher retention rates.",
          tooltip: "PwC Global Wealth Report (2024): HNW clients with AI-assisted tools have 22% higher retention vs those on traditional platforms. For a $500M AUM book, a 1% retention improvement is worth $5M+ in retained AUM annually.",
        },
        {
          title: "Cross-sell opportunity",
          body: "Partners using the white-label interface can cross-sell premium research tiers, strategy bundles, and additional broker account slots — generating incremental revenue from an existing client base at near-zero acquisition cost.",
        },
      ],
    },

    /* ── 4  MARKET OPPORTUNITY ── */
    {
      id: "market",
      type: "market",
      label: "Market Opportunity",
      title: "Large and growing market for institutional-grade retail tools",
      tam: {
        value: "~20M",
        accounts: 20_000_000,
        label: "active retail trading accounts globally (CFD/FX + Equity)",
        tooltip: "Retail CFD/FX: 6.79M accounts (Investment Trends Q4 2024) + ~15M active retail equity traders (US, EU, APAC) reachable via IBKR integration — less ~2M overlap. CFD grew from 3.5M to 6.79M in 5 years; retail CFD = 14.1% of daily global FX turnover. Sources: BIS Triennial 2022, Investment Trends, IBKR Q4 2024 earnings.",
      },
      adjacentMarket: {
        value: "40M–72M",
        label: "adjacent market: crypto traders, futures & prop firm traders",
        tooltip: "Crypto traders (40–70M): same automation and risk management needs; expansion market. Futures traders (~2M): natural fit for IBKR integration. Prop firm traders (500K–1M): pre-built rule packs, high willingness-to-pay. Total adjacent: 42M–72M.",
      },
      sam: {
        value: "3.5M–4.5M",
        accounts: 4_000_000,
        label: "accounts reachable through initial broker and partner channels",
        tooltip: "MetaTrader/cTrader ecosystems (1.5M–2.6M) + IBKR algo/API segment (~700K) + prop firm community (~300K) + wealth management / SMSF channel (~500K via partner distribution). 30% payer rate applies to TAM/SAM revenue estimates.",
      },
      som: {
        value: "10–20K",
        accountsLow: 10_000,
        accountsHigh: 20_000,
        label: "target accounts via partner distribution in 12–18 months",
        tooltip: "Partner-distributed acquisition is significantly lower CAC than direct retail. A single mid-size broker or wealth platform partnership with 50K clients at 20–30% adoption would exceed this target independently.",
      },
      revenueSizing: {
        currency: "A$",
        payerRate: 0.30,
        arpuScenarios: [65, 80, 120],
        payerRateNote: "TAM/SAM assume 30% payer rate. SOM figures are paid subscribers via partner distribution — no payer-rate discount. Enterprise/AUM-based revenue (Model B) is additive and not reflected in these figures.",
      },
    },

    /* ── 5  TRACTION ── */
    {
      id: "traction",
      type: "traction",
      label: "Validation",
      title: "Working platform, validated with live market execution",
      stats: [
        { value: "1,000+", label: "test trades executed across platform modules" },
        { value: "1.54", label: "profit factor across 205 AI-managed live trades (~12 months)" },
        { value: "1.99:1", label: "avg win/loss payoff ratio — single strategy, live market data" },
      ],
      builtToday: [
        "Working platform with MetaTrader and cTrader integrations",
        "IBKR integration in development (targeted Q3 2026)",
        "Real-time price feeds, historical data, risk controls, journaling",
        "Charting and institutional indicator library",
        "AI-assisted no-code strategy builder with backtesting",
        "Automated trade management: co-pilot and autopilot modes",
        "AI-generated technical and fundamental analysis (on-demand and scheduled)",
        "News aggregation and economic calendar",
        "Scalable cloud infrastructure with automated deployment",
      ],
      caseStudy: {
        title: "Live Execution Case Study",
        body: "We ran an AI-managed strategy on a demo account using live market data via cTrader and MetaTrader APIs. The system executed 205 trades with a profit factor of 1.54, payoff ratio of 1.99:1, win rate of 43.41%, and a net P/L of $20,744. All trades were governed by the same enforced risk controls available to users.",
        note: "Demo account credentials can be provided for independent verification.",
        tooltip: "This used a single strategy configuration. Results are from a demo environment with live market data — not hypothetical backtesting. Past performance on demo does not guarantee live results.",
        image: "ai-bot-casestudy",
      },
    },

    /* ── 6  UNIT ECONOMICS ── */
    {
      id: "unit-economics",
      type: "unitEconomics",
      label: "Unit Economics",
      title: "Projected unit economics at Month 12",
      headline: {
        subs: "1,224",
        mrr: "A$150.6K",
        grossMargin: "A$98.0K",
        cac: "A$80",
        payback: "1.0 mo",
        ltvCac: "18.4×",
        netMonthly: "+A$53.0K/m",
        cashPosition: "A$885.4K",
      },
      tiers: [
        { tier: "Starter", price: "A$65", infra: "A$27", gm: "A$38", retention: "12 mo", ltv: "A$456", ltvCac: "5.7×" },
        { tier: "Pro", price: "A$150", infra: "A$55", gm: "A$95", retention: "18 mo", ltv: "A$1,710", ltvCac: "21.4×" },
        { tier: "Premium", price: "A$350", infra: "A$90", gm: "A$260", retention: "24 mo", ltv: "A$6,240", ltvCac: "78.0×" },
      ],
      weightedArpu: "A$96.32",
      weightedGm: "A$60.37",
      weightedLtv: "A$987 (≈12.3× CAC)",
      tooltip: "Retail SaaS unit economics shown for reference. Enterprise/AUM partnership revenue (Model B) is additive: 15–25 bps on AUM + 20% performance fee share. A $100M AUM partner at 20 bps = A$200K/yr base, before performance. These figures do not include enterprise licensing or AUM-based revenue streams.",
    },

    /* ── 7  COMPETITIVE LANDSCAPE ── */
    {
      id: "competitive",
      type: "competitive",
      label: "Competitive Landscape",
      title: "No existing platform delivers compliance-safe AI research at this price point",
      competitors: [
        { name: "Paxia.ai", noCode: true, aiAssist: true, backtest: true, marketplace: "roadmap", execution: true, multiAsset: "roadmap", agentic: true, mt5: "roadmap", fundamentals: true, news: true },
        { name: "Tradetron", noCode: true, aiAssist: false, backtest: true, marketplace: true, execution: true, multiAsset: "partial", agentic: false, mt5: false, fundamentals: false, news: false },
        { name: "Capitalise.ai", noCode: true, aiAssist: false, backtest: true, marketplace: false, execution: true, multiAsset: "partial", agentic: false, mt5: false, fundamentals: false, news: false },
        { name: "MetaTrader 5", noCode: false, aiAssist: false, backtest: true, marketplace: true, execution: true, multiAsset: true, agentic: false, mt5: true, fundamentals: "partial", news: true },
        { name: "QuantConnect", noCode: false, aiAssist: false, backtest: true, marketplace: "community", execution: true, multiAsset: true, agentic: false, mt5: false, fundamentals: true, news: "partial" },
        { name: "TradingView", noCode: false, aiAssist: false, backtest: true, marketplace: true, execution: true, multiAsset: true, agentic: false, mt5: false, fundamentals: true, news: true },
      ],
      features: [
        { key: "noCode", label: "No-code Strategy Builder" },
        { key: "aiAssist", label: "AI Research Generation" },
        { key: "backtest", label: "Backtesting" },
        { key: "marketplace", label: "Strategy Marketplace" },
        { key: "execution", label: "Live Execution" },
        { key: "multiAsset", label: "Multi-asset" },
        { key: "agentic", label: "Human-in-Loop Automation" },
        { key: "mt5", label: "MT5 Integration" },
        { key: "fundamentals", label: "Fundamentals" },
        { key: "news", label: "News / Calendar" },
      ],
      moat: [
        {
          title: "Compliance architecture that regulators accept",
          body: "Human-in-the-loop is not a feature — it is the core architecture. Every AI action requires explicit human authorisation before execution. This makes Paxia the only platform partners can deploy under an MDA/SMA structure without restructuring their compliance framework.",
        },
        {
          title: "Partner-aligned commercial model",
          body: "Revenue structures are designed around partner economics: AUM bps for Model B, 70/30 rev-share for Model A. We succeed when partners succeed. No conflicting incentives — we do not generate revenue from trade volume.",
        },
        {
          title: "No client funds custody — ever",
          body: "Paxia sends trade instructions via broker APIs; it has no mechanism to access, hold, or transfer client funds. This architectural constraint eliminates custody risk for partners and removes a significant regulatory compliance burden.",
        },
        {
          title: "IBKR-first global execution",
          body: "IBKR integration (in development) means partners are not constrained to the Australian CFD market. Models can be deployed across US, EU, and Asian equities, ETFs, and derivatives — broadening the client segment partners can serve.",
        },
        {
          title: "Explainable, auditable AI",
          body: "Research outputs include strategy rationale, signal basis, and risk parameters — not a black-box score. Every decision is logged. Advisers can explain every position to clients and regulators. This is a regulatory necessity, not a nice-to-have.",
        },
        {
          title: "Build cost vs. partnership cost",
          body: "Building equivalent in-house capability costs $2M–$5M+ and 18–24 months minimum. Paxia delivers working, compliant infrastructure via a partnership agreement, with shared ongoing development cost.",
          tooltip: "Estimated build cost breakdown: quant engineering team ($800K–1.5M/yr), data infrastructure ($200K+/yr), broker API integrations ($200–400K build), compliance overlay ($150K+/yr). Ongoing maintenance is typically 30–40% of build cost annually.",
        },
      ],
    },

    /* ── 8  TEAM ── */
    {
      id: "team",
      type: "team",
      label: "Team",
      title: "Experience across trading systems, engineering, and financial services",
      members: [
        {
          name: "Ehsan Paksima",
          role: "Founder",
          photo: ehsanThumb,
          linkedin: "https://www.linkedin.com/in/ehsanpaksima/",
          highlights: [
            "15 years leading technology operations; delivered $7M+ in cost savings through automation and system re-architecture",
            "Solutions architect with experience leading full-stack engineering teams across multiple companies",
            "Active trader for 10+ years across MT4/5 and cTrader — platform built from direct practitioner experience",
            "As Director of Technology at a SaaS startup, increased profit 130% in 14 months through new product development and OPEX reduction",
            "Built and managed distributed engineering teams (onshore and offshore)",
          ],
        },
        {
          name: "Erfan Paksima",
          role: "Co-founder — Engineering",
          photo: erfanThumb,
          linkedin: "https://www.linkedin.com/in/erfan-paksima-670676147/",
          highlights: [
            "Senior full-stack developer and solutions architect",
            "Specialist in backend systems, DevOps automation, and applied machine learning",
            "10 years active trading across multiple markets and platforms",
            "Built CI/CD pipelines, monitoring systems, and cost-optimised cloud infrastructure for multiple startups",
            "Helped multiple startups resolve scaling, data, and infrastructure bottlenecks",
          ],
        },
        {
          name: "Geoffrey Emerson",
          role: "CMO / CX",
          photo: geoffThumb,
          linkedin: "https://www.linkedin.com/in/emersongeoffrey/",
          highlights: [
            "Worked with NSW Office of AI; runs PERCI.IO AI consultancy",
            "Award-winning Head of Digital with experience across fintech and SaaS",
            "Specialises in digital audits, service design, and partnership development",
            "Background in social search, content strategy, and go-to-market execution",
            "Track record of brand growth and partner acquisition in competitive B2B markets",
          ],
        },
      ],
    },

    /* ── 9  ROADMAP ── */
    {
      id: "roadmap",
      type: "roadmap",
      label: "Integration Roadmap",
      title: "Partnership readiness: 12–18 months",
      phases: [
        {
          label: "Now (0–3 months)",
          subtitle: "Partner onboarding & pilot preparation",
          items: [
            "API documentation and white-label integration guide",
            "Billing and account management infrastructure",
            "SOC 2 preparation and penetration testing",
            "Pilot onboarding for first 2–3 broker or wealth partners",
            "IBKR integration development begins",
          ],
          kpis: "2–3 signed pilot partners, API documentation published, SOC 2 audit initiated",
        },
        {
          label: "Next (3–9 months)",
          subtitle: "IBKR launch & MDA capability",
          items: [
            "IBKR integration live (equities, ETFs, derivatives)",
            "MDA/SMA compliance framework documented and partner-ready",
            "Strategy marketplace for partner-curated research bundles",
            "Co-branded client onboarding flows",
            "IG and FXCM broker integrations",
          ],
          kpis: "IBKR live, 1–2 MDA pilots active, 500+ partner-distributed client accounts",
        },
        {
          label: "Later (9–18 months)",
          subtitle: "Scale, enterprise & AFSL",
          items: [
            "Own AFSL application or CAR consolidation",
            "Proprietary ML models for mid-frequency systematic strategies",
            "Prop-firm and institutional pilot programs",
            "Portfolio dashboard, team roles, and compliance reporting suite",
            "Enterprise SLA guarantees and dedicated support tiers",
          ],
          kpis: "3–5 enterprise partners, A$50M+ AUM under Paxia-powered models, AFSL or CAR active",
        },
      ],
    },

    /* ── 10  RISKS ── */
    {
      id: "risks",
      type: "risks",
      label: "Risks & Mitigations",
      title: "Known risks — and how the architecture addresses them",
      items: [
        {
          risk: "Fiduciary & regulatory risk from autonomous AI",
          mitigation: "Human-in-the-loop architecture is non-negotiable. All proposed execution parameters require explicit authorisation before routing to any broker. No autonomous trading without human sign-off. Full audit trail for every decision.",
          tooltip: "MDA regulations under the Corporations Act require documented, supervised discretionary decision-making. Paxia's architecture satisfies this by design — AI generates research, humans approve execution. This is the core differentiator for partner deployments.",
        },
        {
          risk: "Algorithmic model degradation over time",
          mitigation: "Platform focuses on mid-to-low frequency models (equities, ETFs, structured derivatives) rather than HFT. Robust across standard IBKR connections. Model performance is monitored continuously with drift alerts and partner reporting.",
        },
        {
          risk: "Client data privacy and isolation",
          mitigation: "Enterprise-grade isolated data environments. No cross-client data sharing. Privacy documentation aligned with Australian Privacy Act and GDPR. SOC 2 audit in progress.",
        },
        {
          risk: "Broker API variability and execution reliability",
          mitigation: "Abstraction layer per broker with automated sandbox testing before each release. Each integration includes a QA test harness and uptime monitoring. 99.9% feed uptime target from Month 12.",
          tooltip: "Each broker's API has unique behaviours, rate limits, and error handling. The adapter pattern isolates broker-specific logic so the core research and compliance layer is unchanged across integrations.",
        },
        {
          risk: "Client funds custody risk",
          mitigation: "Paxia never holds, transfers, or has access to client funds at any stage. Orders route directly from the platform to the client's own broker account. This is an architectural constraint — the system has no mechanism to handle deposits or withdrawals.",
          tooltip: "Paxia is a SaaS research and execution overlay. The client's money remains with their regulated broker at all times. This eliminates custody risk for partners and removes the need for Paxia to obtain a financial services licence to hold client funds.",
        },
        {
          risk: "Partner regulatory framework compatibility",
          mitigation: "Model A (white-label API) requires no change to partner's AFSL scope — research and signal generation is not a financial product. Model B (CAR/MDA) is structured with legal review to operate within the partner's existing authorisation. Own AFSL application within 12–18 months.",
        },
      ],
    },

    /* ── 11  THE ASK ── */
    {
      id: "ask",
      type: "ask",
      label: "Next Steps",
      title: "Opening targeted discussions with select partners",
      raising: "Pilot Program",
      instrument: "Partnership Agreement",
      runway: "Structured 90-day pilot → commercial agreement pathway",
      useOfFunds: [
        { label: "Technical Integration", pct: 35, detail: "Dedicated engineering support for API integration, white-label setup, and broker connectivity for the pilot partner" },
        { label: "Compliance & Legal", pct: 25, detail: "Legal review of partnership structure, MDA/CAR documentation, AFSL alignment, and privacy framework" },
        { label: "Product & IBKR", pct: 25, detail: "Accelerate IBKR integration and MDA-specific compliance reporting features required for institutional deployment" },
        { label: "Joint Go-to-Market", pct: 15, detail: "Co-branded materials, client onboarding flows, and joint marketing to the partner's existing client base" },
      ],
      hiringPlan: [
        { role: "Partnership Lead", detail: "Dedicated point of contact for integration, onboarding, and commercial negotiation" },
        { role: "Integration Engineer", detail: "Assigned to partner API and white-label setup during pilot period" },
        { role: "Compliance Adviser", detail: "External counsel engaged for AFSL/CAR structuring and MDA documentation" },
        { role: "Pilot Timeline", detail: "90-day technical pilot → performance review → commercial agreement negotiation" },
      ],
    },
  ],
};

export default pitchDeck;
